"use client"
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  BookOpen,
  Users,
  Plus,
  Clock,
  Calendar,
  GraduationCap,
  PenTool,
  ClipboardList,
  MessageCircle,
  Zap,
  PlusSquare,
  Video,
  Loader2,
  X,
  Check,
  Copy,
  Trash2,
  FileText,
  Brain,
  AlertCircle,
  Play,
  Share,
  Edit,
  Save,
  Sparkles,
  ArrowRight,
  UserPlus,
} from "lucide-react"
// Import your actual API functions
import { postAws } from "../postAws"
import { generatePresignedUrl } from "../postFileAws"

const TeacherDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateClassModal, setShowCreateClassModal] = useState(false)
  const [isCreatingClass, setIsCreatingClass] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)
  const [profileImageUrl, setProfileImageUrl] = useState("")
  const [selectedClass, setSelectedClass] = useState(null)
  const [showClassDetails, setShowClassDetails] = useState(false)
  const [isDeletingClass, setIsDeletingClass] = useState(false)
  const [classToDelete, setClassToDelete] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [isEditingSchedule, setIsEditingSchedule] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState([])
  const [isSavingSchedule, setIsSavingSchedule] = useState(false)
  const navigate = useNavigate()

  const [newClassData, setNewClassData] = useState({
    classValue: "",
    subject: "",
    batchSize: "",
    section: "",
    classDescription: "",
    teacherId: localStorage.getItem("teacherId") || "",
    organisationId: localStorage.getItem("organisationId") || "",
    schedule: [],
  })
  const [isOtherClass, setIsOtherClass] = useState(false)
  const [isOtherSubject, setIsOtherSubject] = useState(false)

  // New states for Join Organisation modal
  const [showJoinOrgModal, setShowJoinOrgModal] = useState(false)
  const [orgRequestData, setOrgRequestData] = useState({
    organisationId: "",
    confirmed: false, // For handling the confirmation step
  })
  const [isSubmittingOrgRequest, setIsSubmittingOrgRequest] = useState(false)
  const [orgRequestMessage, setOrgRequestMessage] = useState(null) // For messages inside the modal

  const classOptions = ["9", "10", "11", "12", "Other"]
  const subjectOptions = [
    "Mathematics",
    "Science",
    "Social Science",
    "English",
    "Hindi",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "Other",
  ]
  const daysOfWeek = [
    { id: "monday", name: "Monday", short: "Mon" },
    { id: "tuesday", name: "Tuesday", short: "Tue" },
    { id: "wednesday", name: "Wednesday", short: "Wed" },
    { id: "thursday", name: "Thursday", short: "Thu" },
    { id: "friday", name: "Friday", short: "Fri" },
    { id: "saturday", name: "Saturday", short: "Sat" },
    { id: "sunday", name: "Sunday", short: "Sun" },
  ]

  // Handle class card click
  const handleClassClick = (classItem) => {
    // If this class item comes from today's schedule, it might have schedule data in a different structure
    const processedClassItem = { ...classItem }
    // Check if this is from today's schedule and has scheduleSlot data
    if (classItem.scheduleSlot && !classItem.schedule) {
      // Convert scheduleSlot to schedule array format
      const day = daysOfWeek.find(
        (d) => d.name.toLowerCase() === new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase(),
      )
      processedClassItem.schedule = [
        {
          day: day?.id || "monday",
          startTime: classItem.scheduleSlot.startTime,
          endTime: classItem.scheduleSlot.endTime,
        },
      ]
    }
    // If the class doesn't have complete schedule data, try to find it from the main classes array
    if (!processedClassItem.schedule || processedClassItem.schedule.length === 0) {
      const fullClassData = classes?.find((c) => c.classRoomId === classItem.classRoomId)
      if (fullClassData && fullClassData.schedule) {
        processedClassItem.schedule = fullClassData.schedule
      }
    }
    setSelectedClass(processedClassItem)
    setShowClassDetails(true)
    // Initialize editing schedule with current schedule or empty array
    setEditingSchedule(processedClassItem.schedule || [])
    setIsEditingSchedule(false)
  }

  // Handle schedule editing
  const handleEditSchedule = () => {
    setIsEditingSchedule(true)
    // If no schedule exists, add one empty slot
    if (!editingSchedule || editingSchedule.length === 0) {
      setEditingSchedule([
        {
          id: Date.now(),
          day: "",
          startTime: "",
          endTime: "",
        },
      ])
    }
  }

  const addScheduleSlotToEdit = () => {
    const lastSlot = editingSchedule[editingSchedule.length - 1]
    setEditingSchedule([
      ...editingSchedule,
      {
        id: Date.now(),
        day: "", // Day is not copied, user can change it
        startTime: lastSlot ? lastSlot.startTime : "",
        endTime: lastSlot ? lastSlot.endTime : "",
      },
    ])
  }

  const removeScheduleSlotFromEdit = (id) => {
    setEditingSchedule(editingSchedule.filter((slot) => slot.id !== id))
  }

  const updateEditingScheduleSlot = (id, field, value) => {
    setEditingSchedule(editingSchedule.map((slot) => (slot.id === id ? { ...slot, [field]: value } : slot)))
  }

  const saveScheduleChanges = async () => {
    setIsSavingSchedule(true)
    try {
      // Prepare schedule data - remove id field and ensure all required fields are present
      const scheduleToSave = editingSchedule
        .filter((slot) => slot.day && slot.startTime && slot.endTime)
        .map(({ id, ...slot }) => slot)
      const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", {
        request: "edit-classroom",
        body: {
          classRoomId: selectedClass.classRoomId,
          teacherId: localStorage.getItem("teacherId"),
          schedule: scheduleToSave,
        },
      })
      if (response.statusCode === 200) {
        setFlashMessage({
          type: "success",
          message: "Schedule updated successfully!",
        })
        // Update the selected class with new schedule
        const updatedClass = { ...selectedClass, schedule: scheduleToSave }
        setSelectedClass(updatedClass)
        // Refresh dashboard data
        fetchDashboardData() // Re-fetch to update main classes array
        setIsEditingSchedule(false)
      } else {
        throw new Error(response.body?.message || "Failed to update schedule")
      }
    } catch (error) {
      console.error("Error updating schedule:", error)
      setFlashMessage({
        type: "error",
        message: "Failed to update schedule. Please try again.",
      })
    } finally {
      setIsSavingSchedule(false)
    }
  }

  const cancelScheduleEdit = () => {
    setIsEditingSchedule(false)
    setEditingSchedule(selectedClass.schedule || [])
  }

  // Handle delete class
  const handleDeleteClass = (e, classInfo) => {
    e.stopPropagation() // Prevent triggering the class details modal
    setClassToDelete(classInfo)
    setShowDeleteConfirmation(true)
  }

  const confirmDeleteClass = async () => {
    if (classToDelete && classToDelete.confirmText === "permanently delete") {
      setIsDeletingClass(true)
      try {
        const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", {
          request: "delete-class",
          body: {
            classId: classToDelete.classRoomId,
            teacherId: localStorage.getItem("teacherId"),
          },
        })
        if (response.statusCode === 200) {
          setFlashMessage({
            type: "success",
            message: "Class deleted successfully!",
          })
          // Refresh dashboard data
          fetchDashboardData() // Re-fetch to update main classes array
        } else {
          throw new Error(response.body.message || "Failed to delete class")
        }
      } catch (error) {
      } finally {
        setIsDeletingClass(false)
        setShowDeleteConfirmation(false)
        setClassToDelete(null)
      }
    }
  }

  // Fetch dashboard data using the new single API endpoint
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true)
      const teacherId = localStorage.getItem("teacherId")
      if (!teacherId) {
        setFlashMessage({
          type: "error",
          message: "Teacher ID not found. Please login again.",
        })
        return
      }
      // Single API call to get all dashboard data
      const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", {
        request: "return-teacher-dashboard",
        body: {
          teacherId: teacherId,
        },
      })
      if (response.statusCode === 200) {
        // Parse the body string into a JSON object
        const parsedBody = JSON.parse(response.body)
        const data = parsedBody.data // Access the 'data' property from the parsed object

        setDashboardData(data)
        // Update localStorage with organization data if available
        if (
          data.teacher.organisationId &&
          data.teacher.organisationName &&
          data.teacher.enrollmentRequestToOrg === "approved"
        ) {
          localStorage.setItem("organisationId", data.teacher.organisationId)
          localStorage.setItem("organisationName", data.teacher.organisationName)
          setNewClassData((prev) => ({
            ...prev,
            organisationId: data.teacher.organisationId,
          }))
        } else {
          // Clear organization data if not present or approved
          localStorage.removeItem("organisationId")
          localStorage.removeItem("organisationName")
          setNewClassData((prev) => ({
            ...prev,
            organisationId: "",
          }))
        }

        // Get profile image if available
        if (data.teacher.profilePhotoS3Uri) {
          try {
            const presignedUrl = await generatePresignedUrl(data.teacher.profilePhotoS3Uri)
            setProfileImageUrl(presignedUrl)
          } catch (error) {
            console.error("Error generating presigned URL:", error)
          }
        }
      } else {
        throw new Error(response.body?.message || "Failed to fetch dashboard data")
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to fetch dashboard data. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const addScheduleSlot = () => {
    const lastSlot = newClassData.schedule[newClassData.schedule.length - 1]
    setNewClassData({
      ...newClassData,
      schedule: [
        ...newClassData.schedule,
        {
          id: Date.now(),
          day: "", // Day is not copied, user can change it
          startTime: lastSlot ? lastSlot.startTime : "",
          endTime: lastSlot ? lastSlot.endTime : "",
        },
      ],
    })
  }

  const removeScheduleSlot = (id) => {
    setNewClassData({
      ...newClassData,
      schedule: newClassData.schedule.filter((slot) => slot.id !== id),
    })
  }

  const updateScheduleSlot = (id, field, value) => {
    setNewClassData({
      ...newClassData,
      schedule: newClassData.schedule.map((slot) => (slot.id === id ? { ...slot, [field]: value } : slot)),
    })
  }

  const handleCreateClass = async (e) => {
    e.preventDefault()
    setIsCreatingClass(true)
    const orgId = localStorage.getItem("organisationId") || ""
    const classData = {
      body: {
        ...newClassData,
        classValue: isOtherClass ? newClassData.classValue : newClassData.classValue,
        subject: isOtherSubject ? newClassData.subject : newClassData.subject,
        organisationId: orgId,
        schedule: newClassData.schedule,
      },
      request: "create-Class",
    }
    try {
      const response = await postAws("https://api-dev.aurait.ai/classroom", "POST", classData)
      if (response.statusCode === 201) {
        setShowCreateClassModal(false)
        setFlashMessage({
          type: "success",
          message: "Class created successfully!",
        })
        // Refresh dashboard data
        fetchDashboardData()
        // Reset form
        setNewClassData({
          classValue: "",
          subject: "",
          batchSize: "",
          section: "",
          classDescription: "",
          teacherId: localStorage.getItem("teacherId") || "",
          organisationId: localStorage.getItem("organisationId") || "",
          schedule: [],
        })
        setIsOtherClass(false)
        setIsOtherSubject(false)
      } else {
        throw new Error(response.body?.message || "Failed to create class")
      }
    } catch (err) {
      console.error("Error creating class:", err)
      setFlashMessage({
        type: "error",
        message: err.message || "Failed to create class. Please try again.",
      })
    } finally {
      setIsCreatingClass(false)
    }
  }

  const handleClassChange = (e) => {
    const value = e.target.value
    setIsOtherClass(value === "Other")
    setNewClassData({ ...newClassData, classValue: value === "Other" ? "" : value })
  }

  const handleSubjectChange = (e) => {
    const value = e.target.value
    setIsOtherSubject(value === "Other")
    setNewClassData({ ...newClassData, subject: value === "Other" ? "" : value })
  }

  // Function to handle organization request submission
  const handleJoinOrgSubmit = async (e) => {
    e.preventDefault()
    setIsSubmittingOrgRequest(true)
    try {
      const teacherId = localStorage.getItem("teacherId")
      const hasCreatedClasses = dashboardData?.classes?.length > 0

      // If user has created classes, show confirmation dialog first
      if (hasCreatedClasses && !orgRequestData.confirmed) {
        setOrgRequestMessage({
          type: "warning",
          text: "Warning: If you attach yourself to an organization, your current data will be removed. We recommend creating a new account with a different ID to join an organization instead.",
          needsConfirmation: true,
        })
        setIsSubmittingOrgRequest(false)
        return
      }

      const requestData = {
        request: "teacher-admin-enroll",
        body: {
          userId: teacherId,
          name: dashboardData?.teacher?.teacherName,
          email: dashboardData?.teacher?.teacherEmail,
          phone: dashboardData?.teacher?.phoneNumber,
          organisationId: orgRequestData.organisationId,
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/orgnaisational-admin", "POST", requestData)

      if (response.statusCode === 200) {
        setOrgRequestMessage({
          type: "success",
          text: "Enrollment request sent successfully! You will be notified when approved.",
        })
        setFlashMessage({
          type: "success",
          message: "Enrollment request sent successfully!",
        })
        // Refresh teacher data to get updated enrollment status
        fetchDashboardData()
        setShowJoinOrgModal(false) // Close modal on success
        setOrgRequestData({ organisationId: "", confirmed: false }) // Reset form
      } else if (response.body?.error?.includes("organisation") || response.body?.message?.includes("organisation")) {
        // Handle case when organization doesn't exist
        setOrgRequestMessage({
          type: "error",
          text: "No such organisation exists. Please check the ID and try again.",
          allowRetry: true,
        })
      } else {
        throw new Error(response.body?.error || "Failed to send enrollment request")
      }
    } catch (error) {
      console.error("Error sending enrollment request:", error)
      setOrgRequestMessage({
        type: "error",
        text: error.message || "Failed to send enrollment request. Please try again.",
        allowRetry: true,
      })
      setFlashMessage({
        type: "error",
        message: error.message || "Failed to send enrollment request. Please try again.",
      })
    } finally {
      setIsSubmittingOrgRequest(false)
    }
  }

  // Function to confirm organization request after warning
  const confirmOrgRequest = () => {
    setOrgRequestData((prev) => ({
      ...prev,
      confirmed: true,
    }))
    // Re-submit the form with confirmation
    handleJoinOrgSubmit(new Event("submit")) // Pass a dummy event
  }

  const getQuickActions = () => {
    const baseActions = [
      {
        icon: PenTool,
        label: "Create Quiz",
        color: "from-orange-400 to-pink-500",
        onClick: () => navigate("/quizzes/description"),
      },
      {
        icon: ClipboardList,
        label: "New Assignment",
        color: "from-green-400 to-blue-500",
        onClick: () => navigate("/assignments/description"),
      },
      {
        icon: MessageCircle,
        label: "Send Announcement",
        color: "from-yellow-400 to-red-500",
        onClick: () => navigate("/announcements"),
      },
      {
        icon: PlusSquare,
        label: "Create Class",
        color: "from-teal-400 to-cyan-500",
        onClick: () => setShowCreateClassModal(true),
      },
      {
        icon: Video,
        label: "Start Live Class",
        color: "from-rose-400 to-pink-500",
        onClick: () => window.open("https://pathshala.aurait.ai/", "_blank"),
      },
    ]

    const teacherHasApprovedOrg =
      dashboardData?.teacher?.organisationId && dashboardData?.teacher?.enrollmentRequestToOrg === "approved"

    const hasPendingRequest = dashboardData?.teacher?.enrollmentRequestToOrg === "sent"
    const organisationName = dashboardData?.teacher?.organisationName // Get org name for pending message

    if (teacherHasApprovedOrg) {
      return [
        ...baseActions,
        {
          icon: BookOpen,
          label: "Create Notes",
          color: "from-indigo-400 to-purple-500",
          onClick: () => navigate("/notes"),
        },
      ]
    } else if (hasPendingRequest) {
      return [
        ...baseActions,
        {
          icon: UserPlus,
          label: "View Org Request", // Changed label
          color: "from-amber-400 to-orange-500", // Different color for pending
          onClick: () => {
            setShowJoinOrgModal(true)
            setOrgRequestMessage(null) // Clear previous messages
            setOrgRequestData({ organisationId: "", confirmed: false }) // Reset form
          },
        },
      ]
    } else {
      // No approved org and no pending request
      return [
        ...baseActions,
        {
          icon: UserPlus,
          label: "Join Organisation",
          color: "from-indigo-400 to-purple-500",
          onClick: () => {
            setShowJoinOrgModal(true)
            setOrgRequestMessage(null) // Clear previous messages
            setOrgRequestData({ organisationId: "", confirmed: false }) // Reset form
          },
        },
      ]
    }
  }

  const getSubjectColor = (subject) => {
    const colors = {
      Mathematics: "from-purple-500 to-purple-600",
      Science: "from-green-500 to-green-600",
      "Social Science": "from-yellow-500 to-yellow-600",
      English: "from-blue-500 to-blue-600",
      Hindi: "from-orange-500 to-orange-600",
      Physics: "from-violet-500 to-purple-600",
      Chemistry: "from-emerald-500 to-teal-600",
      Biology: "from-emerald-500 to-emerald-600",
      "Computer Science": "from-cyan-500 to-cyan-600",
      maths: "from-purple-500 to-purple-600",
      eng: "from-blue-500 to-blue-600",
      "Development Team": "from-indigo-500 to-indigo-600",
    }
    return colors[subject] || "from-gray-500 to-gray-600"
  }

  const formatScheduleDisplay = (schedule) => {
    if (!schedule || schedule.length === 0) return "No schedule set"
    return schedule
      .map((slot) => {
        const day = daysOfWeek.find((d) => d.id === slot.day)
        return `${day?.short} ${slot.startTime}-${slot.endTime}`
      })
      .join(", ")
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Loader2 size={40} className="text-indigo-600 animate-spin" />
          <p className="text-indigo-800 font-medium text-lg">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center justify-center space-y-4">
          <AlertCircle size={40} className="text-red-600" />
          <p className="text-red-800 font-medium text-lg">Failed to load dashboard data</p>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const { teacher, stats, classes, assignments, quizzes, recentActivities, todaysSchedule } = dashboardData
  // Check if user has no classes
  const hasNoClasses = !classes || classes.length === 0

  // Prepare stats for display
  const statsData = [
    {
      title: "Active Classes",
      value: stats.totalClasses.toString(),
      subtitle: `${stats.totalClasses} classes created`,
      icon: BookOpen,
      color: "from-blue-500 to-cyan-500",
      trend: "+12%",
    },
    {
      title: "Total Students",
      value: stats.totalEnrolledStudents.toString(),
      subtitle: `${stats.totalEnrolledStudents} enrolled students`,
      icon: Users,
      color: "from-emerald-500 to-green-500",
      trend: "+6%",
    },
    {
      title: "Unshared Content",
      value: stats.unsharedTotal.toString(),
      subtitle: `${stats.unsharedAssignments} assignments, ${stats.unsharedQuizzes} quizzes`,
      icon: Share,
      color: "from-orange-500 to-red-500",
      trend: "Review needed",
    },
  ]

  const teacherHasApprovedOrg =
    teacher?.organisationId && teacher?.organisationName && teacher?.enrollmentRequestToOrg === "approved"

  return (
    <div className="min-h-screen bg-blue-100 relative">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 transform transition-all duration-500 ease-in-out animate-fade-in ${
            flashMessage.type === "error" ? "bg-red-500" : "bg-green-500"
          } text-white max-w-xs sm:max-w-sm`}
        >
          {flashMessage.type === "error" ? (
            <X className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Check className="h-5 w-5 flex-shrink-0" />
          )}
          <p className="text-sm">{flashMessage.message}</p>
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-pink-300/20 to-rose-300/20 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-sky-300/20 to-cyan-300/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-purple-300/10 to-pink-300/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        ></div>
      </div>

      {/* Centered Create Class Banner - Only show when no classes */}
      {hasNoClasses && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="relative overflow-hidden bg-white rounded-3xl shadow-2xl max-w-2xl w-full mx-4 transform transition-all duration-500 animate-scale-in">
            {/* Animated background elements */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50"></div>
            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-indigo-200/30 to-purple-200/30 rounded-full blur-2xl animate-pulse"></div>
            <div
              className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-2xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-gradient-to-br from-cyan-200/20 to-blue-200/20 rounded-full blur-xl animate-pulse"
              style={{ animationDelay: "2s" }}
            ></div>
            {/* Main content */}
            <div className="relative p-12 text-center">
              {/* Animated icon container */}
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-full shadow-2xl transform hover:scale-110 transition-transform duration-300 mx-auto w-fit">
                  <BookOpen className="w-16 h-16 text-white" />
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-pink-400 to-rose-500 p-2 rounded-full shadow-lg animate-bounce">
                    <Sparkles className="w-4 h-4" />
                  </div>
                </div>
              </div>
              {/* Main heading with gradient text */}
              <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                Welcome to Your Teaching Journey!
              </h2>
              {/* Subtitle */}
              <p className="text-xl text-gray-600 mb-3">You haven't created any classes yet</p>
              <p className="text-gray-500 max-w-md mx-auto mb-10 text-lg leading-relaxed">
                Create your first class to start managing your teaching schedule, connect with students, and build an
                amazing learning experience.
              </p>
              {/* Animated CTA button */}
              <button
                onClick={() => setShowCreateClassModal(true)}
                className="group relative px-10 py-5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl font-bold text-xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                {/* Button background animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {/* Button content */}
                <div className="relative flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl group-hover:bg-white/30 transition-colors duration-300">
                    <Plus className="w-6 h-6" />
                  </div>
                  <span>Create Your First Class</span>
                  <ArrowRight className="w-6 h-6 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
                {/* Shine effect */}
                <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              </button>
              {/* Additional encouragement text */}
              <p className="text-sm text-gray-500 mt-8 italic flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Start your teaching journey today!
                <Sparkles className="w-4 h-4" />
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content with conditional blur */}
      <div className="relative z-10 flex flex-col min-h-screen">
        <main className={`flex-1 p-4 sm:p-6 lg:p-8 overflow-auto ${hasNoClasses ? "blur-sm" : ""}`}>
          <div className="space-y-6 lg:space-y-8">
            {/* First Row - Stats Grid - RESPONSIVE */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {statsData.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 p-4 sm:p-6 lg:p-8 rounded-2xl lg:rounded-3xl hover:bg-gray-50 transition-all group hover:scale-105 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-3 lg:mb-4">
                    <div
                      className={`p-2 sm:p-3 lg:p-4 bg-gradient-to-r ${stat.color} rounded-xl lg:rounded-2xl shadow-md group-hover:scale-110 transition-transform`}
                    >
                      <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-white" />
                    </div>
                  </div>
                  <h3 className="text-gray-500 text-xs sm:text-sm font-medium mb-1 lg:mb-2">{stat.title}</h3>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs sm:text-sm">{stat.subtitle}</p>
                </div>
              ))}
            </div>

            {/* Second Row - Today's Schedule, Recent Activities, Quick Actions - RESPONSIVE */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Today's Schedule - First */}
              <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded-2xl lg:rounded-3xl shadow-xl">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <Calendar className="h-5 w-5 lg:h-6 lg:w-6 text-green-500" />
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800">Today's Schedule</h3>
                  </div>
                </div>
                <div className="space-y-3 lg:space-y-4">
                  {todaysSchedule && todaysSchedule.length > 0 ? (
                    todaysSchedule.map((classItem, index) => (
                      <div
                        key={`${classItem.classRoomId}-${index}`}
                        className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => {
                          // Try to get complete class data from the main classes array
                          const completeClassData =
                            classes?.find((c) => c.classRoomId === classItem.classRoomId) || classItem
                          handleClassClick(completeClassData)
                        }}
                      >
                        <div className="text-center flex-shrink-0">
                          <div className="text-sm lg:text-lg font-bold text-gray-800">
                            {classItem.scheduleSlot?.startTime || classItem.startTime}
                          </div>
                          <div className="text-xs text-gray-500">
                            {classItem.scheduleSlot?.endTime || classItem.endTime}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-800 text-sm truncate">
                            {classItem.subject} - {classItem.classValue}
                            {classItem.section}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">{classItem.subject}</p>
                        </div>
                        <button className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors flex-shrink-0">
                          <Play className="h-3 w-3 lg:h-4 lg:w-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 lg:py-8">
                      <Calendar className="h-6 w-6 lg:h-8 lg:w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No classes today</p>
                      <p className="text-gray-400 text-xs">Enjoy your free day!</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Activities - Second */}
              <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded-2xl lg:rounded-3xl shadow-xl">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <Clock className="h-5 w-5 lg:h-6 lg:w-6 text-blue-500" />
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800">Recent Activities</h3>
                  </div>
                </div>
                <div className="space-y-3 lg:space-y-4">
                  {recentActivities && recentActivities.length > 0 ? (
                    recentActivities.slice(0, 5).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 lg:gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div
                          className={`p-2 rounded-lg flex-shrink-0 ${
                            item.type === "assignment"
                              ? "text-orange-600 bg-orange-100"
                              : "text-purple-600 bg-purple-100"
                          }`}
                        >
                          {item.type === "assignment" ? (
                            <FileText className="h-3 w-3 lg:h-4 lg:w-4" />
                          ) : (
                            <Brain className="h-3 w-3 lg:h-4 lg:w-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-800 text-sm truncate">{item.title}</h4>
                          <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                          {item.dueDate && (
                            <p className="text-xs text-gray-400">Due: {new Date(item.dueDate).toLocaleDateString()}</p>
                          )}
                        </div>
                        <span
                          className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
                            item.status === "active" || item.status === "created"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {item.status || "active"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 lg:py-8">
                      <AlertCircle className="h-6 w-6 lg:h-8 lg:w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No recent activities</p>
                      <p className="text-gray-400 text-xs">Create assignments or quizzes to see them here</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions - Third - RESPONSIVE GRID */}
              <div className="bg-white border border-gray-200 p-4 sm:p-6 rounded-2xl lg:rounded-3xl shadow-xl">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <Zap className="h-5 w-5 lg:h-6 lg:w-6 text-yellow-500" />
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800">Quick Actions</h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  {getQuickActions().map((action, index) => (
                    <button
                      key={index}
                      onClick={action.onClick}
                      className={`flex flex-col items-center gap-2 p-3 lg:p-4 bg-gradient-to-r ${action.color} rounded-xl lg:rounded-2xl text-white font-medium shadow-md hover:shadow-xl hover:scale-[1.02] transition-all duration-200`}
                    >
                      <div className="bg-white/20 p-1.5 lg:p-2 rounded-lg lg:rounded-xl">
                        <action.icon className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                      </div>
                      <span className="text-xs text-center leading-tight">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Third Row - My Classes - RESPONSIVE */}
            <div className="bg-white border border-gray-200 p-4 sm:p-6 lg:p-8 rounded-2xl lg:rounded-3xl shadow-lg">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 lg:mb-8 gap-4">
                <div className="flex items-center gap-3 lg:gap-4">
                  <div className="p-2 lg:p-3 bg-gradient-to-r from-purple-400 to-pink-400 rounded-xl lg:rounded-2xl">
                    <GraduationCap className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-800">My Classes</h3>
                </div>
                <button
                  onClick={() => setShowCreateClassModal(true)}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-xl hover:shadow-lg transition-all duration-200 flex items-center gap-2 text-sm lg:text-base"
                >
                  <Plus className="h-4 w-4" />
                  Add Class
                </button>
              </div>
              {classes && classes.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
                  {classes.map((classItem) => (
                    <div
                      key={classItem.classRoomId}
                      className="relative bg-gray-50 p-4 lg:p-6 rounded-2xl lg:rounded-3xl border border-gray-200 hover:bg-white hover:border-gray-300 transition-all group hover:scale-105 shadow-md cursor-pointer"
                      onClick={() => handleClassClick(classItem)}
                    >
                      {/* Delete button - Top Left */}
                      <button
                        onClick={(e) => handleDeleteClass(e, classItem)}
                        className="absolute top-3 lg:top-4 left-3 lg:left-4 p-1.5 lg:p-2 bg-red-50 text-red-500 rounded-full hover:bg-red-100 transition-colors duration-300 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all z-10"
                        aria-label="Delete class"
                      >
                        <Trash2 size={14} className="lg:w-4 lg:h-4" />
                      </button>
                      {/* Class Code - Top Right with proper spacing */}
                      <div className="absolute top-3 lg:top-4 right-3 lg:right-4 flex items-center gap-2 z-10">
                        <span className="text-xs lg:text-sm font-semibold text-gray-600 bg-gray-100 px-2 lg:px-3 py-1 rounded-full border border-gray-300 shadow-sm">
                          {classItem.classCode}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(classItem.classCode)
                            setFlashMessage({
                              type: "success",
                              message: "Class code copied!",
                            })
                          }}
                          title="Copy class code"
                          className="text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          <Copy size={14} className="lg:w-4 lg:h-4" />
                        </button>
                      </div>
                      {/* Main content with proper margins to avoid overlap */}
                      <div className="pr-16 lg:pr-24 mb-4 lg:mb-6 pt-8 lg:pt-0">
                        <div className="flex items-start gap-3 lg:gap-4">
                          <div
                            className={`p-2 lg:p-3 bg-gradient-to-r ${getSubjectColor(classItem.subject)} rounded-xl lg:rounded-2xl text-white text-lg lg:text-xl font-bold flex-shrink-0`}
                          >
                            {classItem.classValue}
                            {classItem.section}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-gray-800 text-base lg:text-lg truncate">
                              Class {classItem.classValue} - {classItem.section}
                            </h4>
                            <p className="text-gray-500 text-sm truncate">{classItem.subject}</p>
                            <p className="text-gray-400 text-xs mt-1 italic truncate">
                              {formatScheduleDisplay(classItem.schedule)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3 lg:space-y-4">
                        <div className="grid grid-cols-2 gap-3 lg:gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Students</span>
                            <p className="text-gray-800 font-semibold text-sm">
                              {classItem.enrolledStudentCount} enrolled
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Content</span>
                            <p className="text-gray-800 font-semibold text-sm">
                              {classItem.assignmentCount + classItem.quizCount} items
                            </p>
                          </div>
                        </div>
                        {/* Organisation Name Display */}
                        {teacherHasApprovedOrg && classItem.organisationId === teacher.organisationId && (
                          <div className="flex items-center justify-between bg-gray-50 p-2.5 rounded-lg">
                            <span className="text-gray-500 text-xs">Organisation:</span>
                            <span className="font-semibold text-gray-800 text-sm">
                              {teacher.organisationName || "N/A"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 lg:py-12 px-4 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <div className="bg-indigo-50 p-3 lg:p-4 rounded-full mb-4">
                    <BookOpen className="w-6 h-6 lg:w-8 lg:h-8 text-indigo-500" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-2">No Classes Created Yet</h3>
                  <p className="text-gray-500 max-w-md mb-4 lg:mb-6 text-sm lg:text-base">
                    Create your first class to start managing your teaching schedule and students.
                  </p>
                  <button
                    onClick={() => setShowCreateClassModal(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center text-sm lg:text-base"
                  >
                    <Plus size={16} className="lg:w-5 lg:h-5 mr-2" />
                    <span>Create Your First Class</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Class Details Modal - RESPONSIVE with Schedule Editing */}
      {showClassDetails && selectedClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 py-4 lg:py-6 px-4 lg:px-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg lg:text-2xl font-bold text-white">
                    {selectedClass.subject} - Class {selectedClass.classValue}
                    {selectedClass.section}
                  </h2>
                  <p className="text-indigo-100 text-sm mt-1">Class Code: {selectedClass.classCode}</p>
                </div>
                <button
                  onClick={() => setShowClassDetails(false)}
                  className="text-white hover:text-indigo-200 transition-colors"
                >
                  <X className="h-5 w-5 lg:h-6 lg:w-6" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-4 lg:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {/* Class Information */}
                <div className="space-y-4 lg:space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-indigo-600" />
                      Class Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subject:</span>
                        <span className="font-medium text-gray-800">{selectedClass.subject}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Class:</span>
                        <span className="font-medium text-gray-800">
                          {selectedClass.classValue}
                          {selectedClass.section}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Class Code:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">{selectedClass.classCode}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(selectedClass.classCode)
                              setFlashMessage({
                                type: "success",
                                message: "Class code copied!",
                              })
                            }}
                            className="text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {selectedClass.classDescription && (
                        <div>
                          <span className="text-gray-600">Description:</span>
                          <p className="font-medium text-gray-800 mt-1">{selectedClass.classDescription}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Schedule Information */}
                <div className="space-y-4 lg:space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-green-600" />
                        Schedule
                      </h3>
                      {!isEditingSchedule && (
                        <button
                          onClick={handleEditSchedule}
                          className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-300 flex items-center gap-1 text-sm font-medium"
                        >
                          <Edit className="h-4 w-4" />
                          {selectedClass.schedule && selectedClass.schedule.length > 0 ? "Edit" : "Add"} Schedule
                        </button>
                      )}
                    </div>
                    {isEditingSchedule ? (
                      <div className="space-y-4">
                        {editingSchedule.length === 0 ? (
                          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <Clock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 text-sm">No schedule slots added yet</p>
                            <button
                              onClick={addScheduleSlotToEdit}
                              className="mt-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center gap-1 mx-auto text-sm"
                            >
                              <Plus className="h-4 w-4" />
                              Add Time Slot
                            </button>
                          </div>
                        ) : (
                          <>
                            {editingSchedule.map((slot) => (
                              <div key={slot.id} className="bg-gray-50 p-4 rounded-lg border">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                                    <select
                                      value={slot.day}
                                      onChange={(e) => updateEditingScheduleSlot(slot.id, "day", e.target.value)}
                                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50 transition-colors"
                                      required
                                    >
                                      <option value="">Select day</option>
                                      {daysOfWeek.map((day) => (
                                        <option key={day.id} value={day.id}>
                                          {day.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                                    <input
                                      type="time"
                                      value={slot.startTime}
                                      onChange={(e) => updateEditingScheduleSlot(slot.id, "startTime", e.target.value)}
                                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50 transition-colors"
                                      required
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                                    <input
                                      type="time"
                                      value={slot.endTime}
                                      onChange={(e) => updateEditingScheduleSlot(slot.id, "endTime", e.target.value)}
                                      className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50 transition-colors"
                                      required
                                    />
                                  </div>
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() => removeScheduleSlotFromEdit(slot.id)}
                                      className="w-full px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-300 flex items-center justify-center"
                                    >
                                      <Trash2 size={16} className="mr-1" />
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={addScheduleSlotToEdit}
                              className="w-full px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-300 flex items-center justify-center gap-1 text-sm font-medium border-2 border-dashed border-green-300"
                            >
                              <Plus size={16} />
                              Add Another Time Slot
                            </button>
                          </>
                        )}
                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                          <button
                            onClick={cancelScheduleEdit}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveScheduleChanges}
                            disabled={isSavingSchedule}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium disabled:bg-green-300 disabled:cursor-not-allowed"
                          >
                            {isSavingSchedule ? (
                              <>
                                <Loader2 className="animate-spin mr-2" size={18} />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Schedule
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {selectedClass.schedule && selectedClass.schedule.length > 0 ? (
                          <div className="space-y-3">
                            {selectedClass.schedule.map((slot, index) => {
                              const day = daysOfWeek.find((d) => d.id === slot.day)
                              return (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                >
                                  <span className="font-medium text-gray-800">{day?.name}</span>
                                  <span className="text-gray-600">
                                    {slot.startTime} - {slot.endTime}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-6 lg:py-8 bg-gray-50 rounded-lg">
                            <Calendar className="h-6 w-6 lg:h-8 lg:w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 text-sm">No schedule set</p>
                            <p className="text-gray-400 text-xs mt-1">
                              Click "Add Schedule" to create your class schedule
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {!isEditingSchedule && (
                <div className="mt-6 lg:mt-8 pt-4 lg:pt-6 border-t flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowClassDetails(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                  >
                    Close
                  </button>
                  <a href="https://pathshala.aurait.ai/" target="_blank" rel="noopener noreferrer">
                    <button className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium">
                      <Video className="h-4 w-4 mr-2" />
                      Start Live Class
                    </button>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - RESPONSIVE */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-600 to-pink-600 py-4 lg:py-6 px-4 lg:px-8">
              <h2 className="text-lg lg:text-2xl font-bold text-white flex items-center">
                <Trash2 className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                Delete Class
              </h2>
              <p className="text-red-100 text-sm mt-1">This action cannot be undone</p>
            </div>
            <div className="p-4 lg:p-8">
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <p className="text-red-800 font-medium">Are you sure you want to delete this class?</p>
                <p className="text-red-600 text-sm mt-1">All associated data will be permanently removed.</p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "permanently delete" to confirm:
                </label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                  placeholder="Type here"
                  onChange={(e) => setClassToDelete({ ...classToDelete, confirmText: e.target.value })}
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false)
                    setClassToDelete(null)
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteClass}
                  disabled={classToDelete?.confirmText !== "permanently delete" || isDeletingClass}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium disabled:bg-red-300 disabled:cursor-not-allowed"
                >
                  {isDeletingClass ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Deleting...
                    </>
                  ) : (
                    "Delete Class"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Class Modal - RESPONSIVE */}
      {showCreateClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 py-4 lg:py-6 px-4 lg:px-8">
              <h2 className="text-lg lg:text-2xl font-bold text-white">Create New Class</h2>
              <p className="text-in digo-100 text-sm mt-1">Fill in the details to create a new class</p>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <form onSubmit={handleCreateClass} className="p-4 lg:p-8">
                <div className="space-y-4 lg:space-y-6">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="className" className="block text-sm font-medium text-gray-700 mb-1">
                        Class
                      </label>
                      <select
                        id="classValue"
                        name="classValue"
                        value={isOtherClass ? "Other" : newClassData.classValue}
                        onChange={handleClassChange}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      >
                        <option value="">Select a class</option>
                        {classOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {isOtherClass && (
                        <input
                          type="text"
                          placeholder="Enter custom class"
                          value={newClassData.classValue}
                          onChange={(e) => setNewClassData({ ...newClassData, classValue: e.target.value })}
                          className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                          required
                        />
                      )}
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={isOtherSubject ? "Other" : newClassData.subject}
                        onChange={handleSubjectChange}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      >
                        <option value="">Select a subject</option>
                        {subjectOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {isOtherSubject && (
                        <input
                          type="text"
                          placeholder="Enter custom subject"
                          value={newClassData.subject}
                          onChange={(e) => setNewClassData({ ...newClassData, subject: e.target.value })}
                          className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                          required
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-1">
                        Section
                      </label>
                      <input
                        type="text"
                        id="section"
                        name="section"
                        value={newClassData.section}
                        onChange={(e) => {
                          const onlyLetters = e.target.value.replace(/[^a-zA-Z]/g, "")
                          setNewClassData({ ...newClassData, section: onlyLetters })
                        }}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        required
                      />
                    </div>
                  </div>
                  {/* Schedule Section */}
                  <div className="border-t pt-4 lg:pt-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-indigo-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-800">Class Schedule</h3>
                      </div>
                      <button
                        type="button"
                        onClick={addScheduleSlot}
                        className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors duration-300 flex items-center text-sm font-medium"
                      >
                        <Plus size={16} className="mr-1" />
                        Add Time Slot
                      </button>
                    </div>
                    {newClassData.schedule.length === 0 ? (
                      <div className="text-center py-6 lg:py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <Clock className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">No schedule added yet</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Click "Add Time Slot" to create your class schedule
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {newClassData.schedule.map((slot) => (
                          <div
                            key={slot.id}
                            className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-xl border border-indigo-100"
                          >
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                                <select
                                  value={slot.day}
                                  onChange={(e) => updateScheduleSlot(slot.id, "day", e.target.value)}
                                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                                  required
                                >
                                  <option value="">Select day</option>
                                  {daysOfWeek.map((day) => (
                                    <option key={day.id} value={day.id}>
                                      {day.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                                <input
                                  type="time"
                                  value={slot.startTime}
                                  onChange={(e) => updateScheduleSlot(slot.id, "startTime", e.target.value)}
                                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                                <input
                                  type="time"
                                  value={slot.endTime}
                                  onChange={(e) => updateScheduleSlot(slot.id, "endTime", e.target.value)}
                                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                                  required
                                />
                              </div>
                              <div>
                                <button
                                  type="button"
                                  onClick={() => removeScheduleSlot(slot.id)}
                                  className="w-full px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-300 flex items-center justify-center"
                                >
                                  <Trash2 size={16} className="mr-1" />
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 lg:mt-8 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 lg:pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateClassModal(false)
                      setNewClassData({
                        classValue: "",
                        subject: "",
                        batchSize: "",
                        section: "",
                        classDescription: "",
                        teacherId: localStorage.getItem("teacherId") || "",
                        organisationId: localStorage.getItem("organisationId") || "",
                        schedule: [],
                      })
                      setIsOtherClass(false)
                      setIsOtherSubject(false)
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium"
                    disabled={isCreatingClass}
                  >
                    {isCreatingClass ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Creating...
                      </>
                    ) : (
                      "Create Class"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Join Organisation Modal */}
      {showJoinOrgModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 py-4 lg:py-6 px-4 lg:px-8">
              <h2 className="text-lg lg:text-2xl font-bold text-white">Join Organisation</h2>
              <p className="text-indigo-100 text-sm mt-1">
                {dashboardData?.teacher?.enrollmentRequestToOrg === "sent" && dashboardData?.teacher?.organisationName
                  ? `Your request to join ${dashboardData.teacher.organisationName} is pending.`
                  : "Enter the organisation ID to send an enrollment request."}
              </p>
            </div>
            <form onSubmit={handleJoinOrgSubmit} className="p-4 lg:p-8">
              {dashboardData?.teacher?.enrollmentRequestToOrg === "sent" && dashboardData?.teacher?.organisationName ? (
                <div className="bg-blue-50 p-4 rounded-lg mb-6 flex items-start">
                  <Check className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <h2 className="text-lg font-bold text-blue-700 mb-1">Request Sent!</h2>
                    <p className="text-blue-600 text-sm">
                      Your request to join{" "}
                      <span className="font-semibold">{dashboardData.teacher.organisationName}</span> has been sent.
                      You'll be added once the admin approves.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {orgRequestMessage ? (
                    <div
                      className={`p-3 rounded-md ${
                        orgRequestMessage.type === "success"
                          ? "bg-green-50 text-green-800"
                          : orgRequestMessage.type === "warning"
                            ? "bg-yellow-50 text-yellow-800"
                            : "bg-red-50 text-red-800"
                      }`}
                    >
                      <p className="text-sm font-medium">{orgRequestMessage.text}</p>
                      {orgRequestMessage.needsConfirmation && (
                        <div className="mt-4 flex space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              setOrgRequestMessage(null)
                              setShowJoinOrgModal(false)
                              setOrgRequestData({ organisationId: "", confirmed: false })
                            }}
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={confirmOrgRequest}
                            className="px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                          >
                            Yes, Continue Anyway
                          </button>
                        </div>
                      )}
                      {orgRequestMessage.allowRetry && (
                        <div className="mt-4 flex space-x-3">
                          <button
                            type="button"
                            onClick={() => setOrgRequestMessage(null)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                          >
                            Try Again
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label htmlFor="organisationId" className="block text-sm font-medium text-gray-700 mb-1">
                        Organisation ID
                      </label>
                      <input
                        type="text"
                        id="organisationId"
                        name="organisationId"
                        value={orgRequestData.organisationId}
                        onChange={(e) => setOrgRequestData({ ...orgRequestData, organisationId: e.target.value })}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 transition-colors"
                        placeholder="Enter organisation ID"
                        required
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowJoinOrgModal(false)
                    setOrgRequestData({ organisationId: "", confirmed: false })
                    setOrgRequestMessage(null)
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                >
                  Close
                </button>
                {!(
                  dashboardData?.teacher?.enrollmentRequestToOrg === "sent" && dashboardData?.teacher?.organisationName
                ) && (
                  <button
                    type="submit"
                    disabled={isSubmittingOrgRequest || orgRequestMessage?.needsConfirmation}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium disabled:bg-indigo-300 disabled:cursor-not-allowed"
                  >
                    {isSubmittingOrgRequest ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Sending Request...
                      </>
                    ) : (
                      "Send Request"
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeacherDashboard
